import React, { useState, useEffect } from 'react';
import axios from "../../Uri";

const BedHistoryPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filterBedId, setFilterBedId] = useState('');
  const [filterGuestId, setFilterGuestId] = useState('');

  useEffect(() => {
    fetchBedHistory();
  }, []);

  const fetchBedHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get('/payment/bedHistoryForVacatedGuests');
      
      // Axios automatically parses JSON, data is in response.data
      const fetchedData = response.data;
      setData(fetchedData);
      setTotalItems(fetchedData.length);
      setLoading(false);
    } catch (err) {
      // Axios error handling
      const errorMessage = err.response 
        ? `HTTP error! status: ${err.response.status} - ${err.response.statusText || err.message}`
        : err.message || 'Failed to fetch bed history data';
      
      setError(errorMessage);
      setLoading(false);
      console.error('Error fetching bed history:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handleFilterBedIdChange = (e) => {
    setFilterBedId(e.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleFilterGuestIdChange = (e) => {
    setFilterGuestId(e.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setFilterBedId('');
    setFilterGuestId('');
    setCurrentPage(1);
  };

  // Filter data based on bedId and guestId filters
  const filteredData = data.filter(item => {
    const bedIdMatch = !filterBedId || 
      item.bedId?.toLowerCase().includes(filterBedId.toLowerCase());
    const guestIdMatch = !filterGuestId || 
      item.guestId?.toLowerCase().includes(filterGuestId.toLowerCase());
    return bedIdMatch && guestIdMatch;
  });

  // Calculate pagination based on filtered data
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Generate page numbers
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Show max 5 page numbers at a time
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  
  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const visiblePageNumbers = pageNumbers.slice(startPage - 1, endPage);

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Bed History for Vacated Guests</h2>
          <div style={styles.controls}>
            <label style={styles.label}>
              Items per page:
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={styles.select}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </label>
            <button
              onClick={fetchBedHistory}
              style={{
                ...styles.refreshButton,
                ...(loading ? styles.refreshButtonDisabled : {})
              }}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div style={styles.error}>
            Error: {error}
          </div>
        )}

        {/* Filters Section */}
        <div style={styles.filterSection}>
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Filter by Guest ID:</label>
              <input
                type="text"
                placeholder="Enter Guest ID..."
                value={filterGuestId}
                onChange={handleFilterGuestIdChange}
                style={styles.filterInput}
              />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Filter by Bed ID:</label>
              <input
                type="text"
                placeholder="Enter Bed ID..."
                value={filterBedId}
                onChange={handleFilterBedIdChange}
                style={styles.filterInput}
              />
            </div>
            {(filterBedId || filterGuestId) && (
              <button
                onClick={handleClearFilters}
                style={styles.clearFilterButton}
              >
                Clear Filters
              </button>
            )}
          </div>
          {(filterBedId || filterGuestId) && (
            <div style={styles.filterInfo}>
              Showing {filteredData.length} of {data.length} records
            </div>
          )}
        </div>

        {loading && !data.length ? (
          <div style={styles.loading}>Loading data...</div>
        ) : (
          <>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeaderRow}>
                    <th style={styles.tableHeader}>Guest ID</th>
                    <th style={styles.tableHeader}>Building ID</th>
                    <th style={styles.tableHeader}>Bed ID</th>
                    <th style={styles.tableHeader}>Check In</th>
                    <th style={styles.tableHeader}>Check Out</th>
                    <th style={styles.tableHeader}>Occupancy Type</th>
                    <th style={styles.tableHeader}>Transaction ID</th>
                    <th style={styles.tableHeader}>Transaction Date</th>
                    <th style={styles.tableHeader}>Amount Paid</th>
                    <th style={styles.tableHeader}>Payment Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={styles.noData}>
                        No data available
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((item, index) => (
                      <tr 
                        key={index} 
                        style={styles.tableRow}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <td style={styles.tableCell}>{item.guestId}</td>
                        <td style={styles.tableCell}>{item.buildingId}</td>
                        <td style={styles.tableCell}>{item.bedId}</td>
                        <td style={styles.tableCell}>{formatDate(item.checkInDate)}</td>
                        <td style={styles.tableCell}>{formatDate(item.checkOutDate)}</td>
                        <td style={styles.tableCell}>
                          <span style={getOccupancyTypeStyle(item.occupancyType)}>
                            {item.occupancyType}
                          </span>
                        </td>
                        <td style={styles.tableCell}>{item.transactionId}</td>
                        <td style={styles.tableCell}>{formatDateTime(item.transactionDate)}</td>
                        <td style={styles.tableCell}>
                          <span style={getAmountStyle(item.amountPaid)}>
                            {formatCurrency(item.amountPaid)}
                          </span>
                        </td>
                        <td style={styles.tableCell}>
                          <span style={getPaymentPurposeStyle(item.paymentPurpose)}>
                            {item.paymentPurpose}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    ...styles.paginationButton,
                    ...(currentPage === 1 ? styles.paginationButtonDisabled : {})
                  }}
                >
                  Previous
                </button>

                {startPage > 1 && (
                  <>
                    <button
                      onClick={() => handlePageChange(1)}
                      style={styles.paginationButton}
                    >
                      1
                    </button>
                    {startPage > 2 && <span style={styles.paginationEllipsis}>...</span>}
                  </>
                )}

                {visiblePageNumbers.map(number => (
                  <button
                    key={number}
                    onClick={() => handlePageChange(number)}
                    style={{
                      ...styles.paginationButton,
                      ...(currentPage === number ? styles.paginationButtonActive : {})
                    }}
                  >
                    {number}
                  </button>
                ))}

                {endPage < totalPages && (
                  <>
                    {endPage < totalPages - 1 && <span style={styles.paginationEllipsis}>...</span>}
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      style={styles.paginationButton}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    ...styles.paginationButton,
                    ...(currentPage === totalPages ? styles.paginationButtonDisabled : {})
                  }}
                >
                  Next
                </button>
              </div>
            )}

            <div style={styles.paginationInfo}>
              Showing {filteredData.length > 0 ? indexOfFirstItem + 1 : 0} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries
            </div>
          </>
        )}
      </div>
    );
};

// Helper functions for styling
const getOccupancyTypeStyle = (type) => {
  const baseStyle = {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500'
  };
  
  if (type === 'Regular') {
    return { ...baseStyle, backgroundColor: '#e3f2fd', color: '#1976d2' };
  } else if (type === 'daily') {
    return { ...baseStyle, backgroundColor: '#fff3e0', color: '#f57c00' };
  }
  return { ...baseStyle, backgroundColor: '#f5f5f5', color: '#666' };
};

const getAmountStyle = (amount) => {
  if (amount > 0) {
    return { color: '#2e7d32', fontWeight: '600' };
  } else if (amount < 0) {
    return { color: '#d32f2f', fontWeight: '600' };
  }
  return { color: '#666' };
};

const getPaymentPurposeStyle = (purpose) => {
  const baseStyle = {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500'
  };
  
  if (purpose === 'OnBoarding') {
    return { ...baseStyle, backgroundColor: '#e8f5e9', color: '#388e3c' };
  } else if (purpose === 'Refund') {
    return { ...baseStyle, backgroundColor: '#ffebee', color: '#c62828' };
  }
  return { ...baseStyle, backgroundColor: '#f5f5f5', color: '#666' };
};

// Styles
const styles = {
  container: {
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  title: {
    margin: 0,
    color: '#333',
    fontSize: '24px',
    fontWeight: '600'
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    flexWrap: 'wrap'
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#666'
  },
  select: {
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '14px',
    cursor: 'pointer'
  },
  refreshButton: {
    padding: '8px 16px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  refreshButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  error: {
    padding: '12px',
    backgroundColor: '#ffebee',
    color: '#c62828',
    borderRadius: '4px',
    marginBottom: '20px',
    border: '1px solid #ef5350'
  },
  filterSection: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  filterRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '20px',
    flexWrap: 'wrap'
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: '1',
    minWidth: '200px'
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    margin: 0
  },
  filterInput: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    outline: 'none'
  },
  clearFilterButton: {
    padding: '8px 16px',
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    height: 'fit-content'
  },
  filterInfo: {
    marginTop: '12px',
    padding: '8px 12px',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#666'
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '1200px'
  },
  tableHeaderRow: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd'
  },
  tableHeader: {
    padding: '12px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#333',
    fontSize: '14px',
    borderRight: '1px solid #e0e0e0'
  },
  tableRow: {
    borderBottom: '1px solid #e0e0e0',
    transition: 'background-color 0.2s'
  },
  tableRowHover: {
    backgroundColor: '#f9f9f9'
  },
  tableCell: {
    padding: '12px',
    fontSize: '14px',
    color: '#555',
    borderRight: '1px solid #f0f0f0'
  },
  noData: {
    textAlign: 'center',
    padding: '40px',
    color: '#999',
    fontSize: '16px'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '20px',
    flexWrap: 'wrap'
  },
  paginationButton: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s',
    minWidth: '40px'
  },
  paginationButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  paginationButtonActive: {
    backgroundColor: '#1976d2',
    color: 'white',
    borderColor: '#1976d2',
    fontWeight: '600'
  },
  paginationEllipsis: {
    padding: '0 8px',
    color: '#666'
  },
  paginationInfo: {
    textAlign: 'center',
    marginTop: '15px',
    color: '#666',
    fontSize: '14px'
  }
};

export default BedHistoryPage;